/**
 * Google Calendar / Outlook Calendar two-way sync for provider schedules (`BACKLOG.md` K7,
 * `docs/decisions/calendar-sync-scope.md`). Wire schemas and closed vocabularies shared by the
 * server routes (`server/src/scheduling/calendarSync/`) and the settings page
 * (`web/src/routes/ProviderCalendarSyncPage.tsx`), matching `messagingRequests.ts`'s/
 * `orgBrandingRequests.ts`'s own reasoning for living in `shared/` rather than beside the routes.
 *
 * **Three-valued per-provider sync state, not a boolean** — `emr-po` E13's own answer, restated
 * here only as the closed vocabulary this file is responsible for: `syncEnabled: false` (off, the
 * default), `syncEnabled: true, phiInclusionEnabled: false` (on, minimum-necessary — "Busy" and
 * nothing else), `syncEnabled: true, phiInclusionEnabled: true` (on, PHI-inclusive — available only
 * with a current BAA on file for that exact provider+vendor pair, re-checked server-side at sync
 * time regardless of what this flag stores — see the decision doc's BAA section).
 *
 * **No column here ever carries a refresh token, encrypted or otherwise.** Every response schema
 * below is what a client may see; `calendar_connections.encryptedRefreshToken` never leaves the
 * server, the same "credential never crosses to the client" rule `documents/storage.ts`'s
 * `getDocumentUrl` and `help/anthropicClient.ts`'s own header both already state for their own
 * secrets.
 */

import { z } from 'zod';

export const CALENDAR_PROVIDERS = ['google', 'microsoft'] as const;
export type CalendarProvider = (typeof CALENDAR_PROVIDERS)[number];

export function isCalendarProvider(value: string): value is CalendarProvider {
  return (CALENDAR_PROVIDERS as readonly string[]).includes(value);
}

export const BAA_RECORD_STATUSES = ['current', 'superseded'] as const;
export type BaaRecordStatus = (typeof BAA_RECORD_STATUSES)[number];

export const CALENDAR_SYNC_DIRECTIONS = ['outbound', 'inbound'] as const;
export type CalendarSyncDirection = (typeof CALENDAR_SYNC_DIRECTIONS)[number];

export const CALENDAR_SYNC_SOURCE_TYPES = ['appointment', 'schedule_block'] as const;
export type CalendarSyncSourceType = (typeof CALENDAR_SYNC_SOURCE_TYPES)[number];

// --- Connections ---------------------------------------------------------------------------------

/** One (org, provider, vendor) OAuth connection, as returned to a client. Never carries the
 * refresh token — see this file's own header. `syncCursor` is internal sync bookkeeping (Google's
 * `syncToken` / Microsoft Graph's `deltaLink`) and is likewise never returned. */
export const calendarConnectionSchema = z.strictObject({
  id: z.uuid(),
  orgId: z.uuid(),
  providerId: z.uuid(),
  calendarProvider: z.enum(CALENDAR_PROVIDERS),
  externalCalendarId: z.string(),
  syncEnabled: z.boolean(),
  phiInclusionEnabled: z.boolean(),
  lastSyncedAt: z.string().nullable(),
  lastSyncError: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CalendarConnection = z.infer<typeof calendarConnectionSchema>;

export const calendarConnectionListResponseSchema = z.strictObject({
  connections: z.array(calendarConnectionSchema),
});
export type CalendarConnectionListResponse = z.infer<typeof calendarConnectionListResponseSchema>;

export const calendarConnectionResponseSchema = z.strictObject({
  connection: calendarConnectionSchema,
});
export type CalendarConnectionResponse = z.infer<typeof calendarConnectionResponseSchema>;

/** `POST /api/calendar-sync/:provider/start` — self-service, no body: the connecting provider is
 * always `session.userId`, never a request-supplied value (`claude-kit/rules/invariants.md`'s
 * "server-authoritative values never come from the request"). Returns the vendor's own consent-
 * screen URL for the SPA to navigate the browser to. */
export const startCalendarConnectionResponseSchema = z.strictObject({
  authorizationUrl: z.url(),
});
export type StartCalendarConnectionResponse = z.infer<typeof startCalendarConnectionResponseSchema>;

/** `PATCH /api/calendar-sync/connections/:connectionId` — self-service toggles, both independent.
 * Neither field is required; a caller sends only what it means to change. Whether
 * `phiInclusionEnabled: true` is actually *honored* at sync time still depends on a current BAA —
 * this schema only shapes what a caller may request, exactly the "UI gate is a courtesy, not the
 * enforcement" split the decision doc draws. */
export const updateCalendarConnectionRequestSchema = z.strictObject({
  syncEnabled: z.boolean().optional(),
  phiInclusionEnabled: z.boolean().optional(),
});
export type UpdateCalendarConnectionRequest = z.infer<typeof updateCalendarConnectionRequestSchema>;

// --- BAA records ----------------------------------------------------------------------------------

/** One provider+vendor BAA-on-file record, as returned to a client. Never carries `storageRef`
 * directly — a client fetches the document itself, if ever needed, through a dedicated
 * time-limited-URL route, matching `documents/storage.ts`'s `getDocumentUrl` posture; this schema
 * only carries the metadata a settings page needs to render current/lapsed status. */
export const providerBaaRecordSchema = z.strictObject({
  id: z.uuid(),
  orgId: z.uuid(),
  providerId: z.uuid(),
  calendarProvider: z.enum(CALENDAR_PROVIDERS),
  uploadedByUserId: z.uuid(),
  uploadedAt: z.string(),
  currentUntil: z.string(),
  status: z.enum(BAA_RECORD_STATUSES),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProviderBaaRecord = z.infer<typeof providerBaaRecordSchema>;

export const providerBaaRecordListResponseSchema = z.strictObject({
  records: z.array(providerBaaRecordSchema),
});
export type ProviderBaaRecordListResponse = z.infer<typeof providerBaaRecordListResponseSchema>;

export const providerBaaRecordResponseSchema = z.strictObject({ record: providerBaaRecordSchema });
export type ProviderBaaRecordResponse = z.infer<typeof providerBaaRecordResponseSchema>;

/** `POST /api/calendar-sync/baa/:providerId/:provider` — a JSON body carrying the file as base64,
 * matching `patientPhotoRequests.ts`'s `uploadPatientPhotoRequestSchema` shape (`documents/
 * patientPhotoRoutes.ts`'s own C18 precedent) rather than `profilePhotoRequests.ts`'s `multipart/
 * form-data` shape — this codebase already has both upload conventions; JSON+base64 is the one
 * reused here. `declaredMimeType` is a fixed single-value enum (never a broader
 * `DOCUMENT_MIME_TYPES` union) — a BAA is a signed legal document, not an image, per the decision
 * doc's own "restricted to `application/pdf`" text. */
export const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const MAX_BAA_DOCUMENT_FILENAME_LENGTH = 255;
/** ~10 MB of decoded PDF bytes, base64-inflated (× 4/3, rounded up generously). */
export const MAX_BAA_DOCUMENT_BASE64_LENGTH = 14_000_000;

export const uploadProviderBaaRequestSchema = z.strictObject({
  filename: z.string().trim().min(1).max(MAX_BAA_DOCUMENT_FILENAME_LENGTH),
  declaredMimeType: z.literal('application/pdf'),
  contentBase64: z.base64().min(1).max(MAX_BAA_DOCUMENT_BASE64_LENGTH),
  currentUntil: z
    .string()
    .regex(CALENDAR_DATE_PATTERN, 'Expected an ISO calendar date, YYYY-MM-DD'),
});
export type UploadProviderBaaRequest = z.infer<typeof uploadProviderBaaRequestSchema>;

// --- Sync conflicts --------------------------------------------------------------------------------

/** One open (or resolved) sync conflict — a booking made in `emr-app` that a later sync run found
 * overlapping an externally-authored `scheduleBlocks` row. Staff-facing, never auto-resolved — see
 * the decision doc's "Sync direction and conflict resolution" section. */
export const calendarSyncConflictSchema = z.strictObject({
  id: z.uuid(),
  orgId: z.uuid(),
  providerId: z.uuid(),
  appointmentId: z.uuid(),
  scheduleBlockId: z.uuid(),
  detectedAt: z.string(),
  resolvedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CalendarSyncConflict = z.infer<typeof calendarSyncConflictSchema>;

export const calendarSyncConflictListResponseSchema = z.strictObject({
  conflicts: z.array(calendarSyncConflictSchema),
});
export type CalendarSyncConflictListResponse = z.infer<
  typeof calendarSyncConflictListResponseSchema
>;

export const calendarSyncConflictResponseSchema = z.strictObject({
  conflict: calendarSyncConflictSchema,
});
export type CalendarSyncConflictResponse = z.infer<typeof calendarSyncConflictResponseSchema>;

// --- Failure vocabulary -----------------------------------------------------------------------------

/** Closed, matching `MessagingErrorCode`'s/`SchedulingErrorCode`'s own shape. */
export const CALENDAR_SYNC_ERROR_CODES = [
  'invalid_request',
  'not_found',
  /** `GOOGLE_CALENDAR_CLIENT_ID`/`_SECRET` (or the Microsoft pair) is unset for this deployment —
   * `config/env.ts`'s optional-plus-503 shape, matching `platform/db.ts`'s own posture for
   * `PLATFORM_OPS_DATABASE_URL`. */
  'provider_not_configured',
  /** The OAuth `state` value failed HMAC verification or had expired — a stale or forged callback,
   * never a real vendor redirect the connect flow itself produced. */
  'invalid_oauth_state',
  /** The vendor's own token exchange refused the authorization code. */
  'oauth_exchange_failed',
  /** `PATCH .../connections/:id` tried to set `phiInclusionEnabled: true` with no current BAA on
   * file for that (provider, vendor) pair — the UI-level courtesy gate the decision doc names;
   * the sync worker enforces the real one independently, at write time. */
  'baa_not_current',
  /** BAA upload: `declaredMimeType` was not `application/pdf`, or the uploaded bytes' own magic-byte
   * signature did not match it — `documents/storage.ts`'s `DocumentContentMismatchError`. */
  'document_content_mismatch',
  'document_malware_detected',
  'audit_unavailable',
] as const;
export type CalendarSyncErrorCode = (typeof CALENDAR_SYNC_ERROR_CODES)[number];

export function isCalendarSyncErrorCode(value: string): value is CalendarSyncErrorCode {
  return (CALENDAR_SYNC_ERROR_CODES as readonly string[]).includes(value);
}

export const calendarSyncErrorResponseSchema = z.strictObject({
  error: z.enum(CALENDAR_SYNC_ERROR_CODES),
});
export type CalendarSyncErrorResponse = z.infer<typeof calendarSyncErrorResponseSchema>;
